import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Copy, KeyRound, Loader2, LogOut, Plus, ShieldOff, Bot } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import AdminTabs from "@/components/AdminTabs";

const ALL_SCOPES = ["drafts", "projects", "companies", "regions"];

const fmt = (iso) =>
  iso ? new Date(iso).toLocaleString("en-CA", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }) : "—";

export default function AdminAgentKeysPage() {
  const nav = useNavigate();
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ name: "", description: "", scopes: [...ALL_SCOPES], rate_limit_per_min: 30 });
  const [creating, setCreating] = useState(false);
  const [newSecret, setNewSecret] = useState(null);

  const load = () => {
    setLoading(true);
    api
      .get("/admin/agent-keys", { withCredentials: true })
      .then((r) => setKeys(Array.isArray(r.data) ? r.data : []))
      .catch(() => toast.error("Failed to load agent keys"))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const logout = async () => {
    try { await api.post("/auth/logout", null, { withCredentials: true }); } catch { /* ignore */ }
    window.location.href = "/login";
  };

  const toggleScope = (s) => {
    setForm((f) => ({
      ...f,
      scopes: f.scopes.includes(s) ? f.scopes.filter((x) => x !== s) : [...f.scopes, s],
    }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error("Name is required"); return; }
    if (!form.scopes.length) { toast.error("Pick at least one scope"); return; }
    setCreating(true);
    try {
      const r = await api.post("/admin/agent-keys", form, { withCredentials: true });
      setNewSecret(r.data);
      setShowCreate(false);
      setForm({ name: "", description: "", scopes: [...ALL_SCOPES], rate_limit_per_min: 30 });
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Create failed");
    } finally { setCreating(false); }
  };

  const revoke = async (id) => {
    if (!window.confirm("Revoke this key? Any agent using it will lose access immediately.")) return;
    try {
      await api.post(`/admin/agent-keys/${id}/revoke`, null, { withCredentials: true });
      toast.success("Key revoked");
      load();
    } catch { toast.error("Revoke failed"); }
  };

  const copy = async (text) => {
    try { await navigator.clipboard.writeText(text); toast.success("Copied"); }
    catch { toast.error("Copy failed"); }
  };

  return (
    <div data-testid="admin-agent-keys-page" className="bg-bb-offwhite min-h-screen">
      <header className="sticky top-0 z-40 bg-white border-b border-border">
        <div className="bb-container py-3 flex items-center justify-between gap-3">
          <div>
            <div className="bb-overline text-bb-copper">Admin · Agent Keys</div>
            <div className="font-archivo font-bold text-bb-navy text-lg leading-tight">
              Agent API — {keys.length} {keys.length === 1 ? "key" : "keys"}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowCreate((v) => !v)}
              data-testid="admin-new-agent-key"
              className="bb-btn-primary inline-flex items-center gap-2 px-3 py-2 text-[11px] min-h-[44px]"
            >
              <Plus size={13} /> <span className="hidden sm:inline">New key</span>
            </button>
            <button
              onClick={logout}
              data-testid="admin-logout"
              className="inline-flex items-center gap-1 px-3 py-2 text-[11px] font-archivo uppercase tracking-widest text-bb-slate hover:text-bb-navy"
            >
              <LogOut size={13} /> <span className="hidden sm:inline">Sign out</span>
            </button>
          </div>
        </div>
        <AdminTabs />
      </header>

      <div className="bb-container py-4 space-y-4">
        {/* One-time secret reveal */}
        {newSecret && (
          <div
            data-testid="new-agent-secret"
            className="bg-white border-2 border-bb-copper p-4 space-y-3"
          >
            <div className="bb-overline text-bb-copper">One-time secret — copy now, it won&apos;t be shown again</div>
            <div className="font-mono text-sm bg-bb-navy text-white px-3 py-2 break-all">
              {newSecret.secret}
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <button
                onClick={() => copy(newSecret.secret)}
                data-testid="copy-agent-secret"
                className="inline-flex items-center gap-1 px-3 py-2 border border-bb-navy text-bb-navy text-sm font-archivo uppercase tracking-widest min-h-[44px]"
              >
                <Copy size={13} /> Copy secret
              </button>
              <button
                onClick={() => setNewSecret(null)}
                data-testid="dismiss-agent-secret"
                className="inline-flex items-center gap-1 px-3 py-2 text-sm font-archivo uppercase tracking-widest text-bb-slate hover:text-bb-navy min-h-[44px]"
              >
                Dismiss
              </button>
            </div>
            <div className="text-[11px] font-mono text-bb-slate">
              Key ID: {newSecret.id} · Scopes: {(newSecret.scopes || []).join(", ")} · Rate limit: {newSecret.rate_limit_per_min}/min
            </div>
          </div>
        )}

        {/* Create form */}
        {showCreate && (
          <form
            onSubmit={submit}
            data-testid="create-agent-key-form"
            className="bg-white border border-border p-4 space-y-3"
          >
            <div className="bb-overline text-bb-copper">New agent key</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <label className="space-y-1">
                <div className="text-[11px] font-archivo uppercase tracking-widest text-bb-slate">Name *</div>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="e.g. research-bot-01"
                  data-testid="agent-key-name"
                  className="w-full border border-border px-3 py-2 text-sm font-mono"
                  required
                />
              </label>
              <label className="space-y-1">
                <div className="text-[11px] font-archivo uppercase tracking-widest text-bb-slate">Rate limit (req/min)</div>
                <input
                  type="number"
                  min="1"
                  max="600"
                  value={form.rate_limit_per_min}
                  onChange={(e) => setForm({ ...form, rate_limit_per_min: parseInt(e.target.value || "30", 10) })}
                  data-testid="agent-key-rate-limit"
                  className="w-full border border-border px-3 py-2 text-sm font-mono"
                />
              </label>
            </div>
            <label className="space-y-1 block">
              <div className="text-[11px] font-archivo uppercase tracking-widest text-bb-slate">Description</div>
              <input
                type="text"
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="What does this agent do?"
                data-testid="agent-key-description"
                className="w-full border border-border px-3 py-2 text-sm"
              />
            </label>
            <div>
              <div className="text-[11px] font-archivo uppercase tracking-widest text-bb-slate mb-2">Scopes</div>
              <div className="flex flex-wrap gap-2">
                {ALL_SCOPES.map((s) => (
                  <button
                    type="button"
                    key={s}
                    onClick={() => toggleScope(s)}
                    data-testid={`agent-key-scope-${s}`}
                    className={`px-3 py-1.5 text-[11px] font-archivo uppercase tracking-widest border ${
                      form.scopes.includes(s)
                        ? "bg-bb-navy text-white border-bb-navy"
                        : "bg-white text-bb-navy border-border"
                    }`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
            <div className="flex items-center gap-2 pt-2 border-t border-border">
              <button
                type="submit"
                disabled={creating}
                data-testid="submit-agent-key"
                className="bb-btn-primary inline-flex items-center gap-2 px-4 py-2 text-sm disabled:opacity-50 min-h-[44px]"
              >
                {creating ? <Loader2 size={13} className="animate-spin" /> : <KeyRound size={13} />} Create key
              </button>
              <button
                type="button"
                onClick={() => setShowCreate(false)}
                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-archivo uppercase tracking-widest text-bb-slate hover:text-bb-navy min-h-[44px]"
              >
                Cancel
              </button>
            </div>
          </form>
        )}

        {/* Key list */}
        {loading ? (
          <div className="text-center py-10 bb-overline text-bb-slate">Loading…</div>
        ) : keys.length === 0 ? (
          <div className="bg-white border border-border p-8 text-center">
            <Bot size={20} className="text-bb-copper mx-auto" />
            <div className="bb-overline text-bb-copper mt-3">No agent keys yet</div>
            <p className="text-sm text-bb-slate mt-2">
              Create a key to let external AI agents submit content via the Agent API.
              Docs: <a href="/api/agent/docs" target="_blank" rel="noreferrer" className="bb-link">/api/agent/docs</a>
            </p>
          </div>
        ) : (
          keys.map((k) => {
            const revoked = !!k.revoked_at;
            return (
              <article
                key={k.id}
                className={`bg-white border p-4 flex flex-col gap-3 ${revoked ? "border-border opacity-70" : "border-border"}`}
                data-testid={`agent-key-${k.id}`}
              >
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="bb-overline text-bb-copper">{k.name}</span>
                    <span
                      className={`text-[10px] font-mono px-2 py-0.5 ${
                        revoked ? "bb-tag-neutral" : "bb-tag-positive"
                      }`}
                      data-testid={`agent-key-status-${k.id}`}
                    >
                      {revoked ? "REVOKED" : "ACTIVE"}
                    </span>
                  </div>
                  <span className="text-[11px] font-mono text-bb-slate">Created {fmt(k.created_at)}</span>
                </div>
                {k.description && <p className="text-sm text-bb-slate">{k.description}</p>}
                <div className="text-[11px] font-mono text-bb-slate flex flex-wrap gap-x-3 gap-y-1">
                  <span>id: {k.id}</span>
                  <span>preview: {k.key_preview}</span>
                  <span>rate: {k.rate_limit_per_min}/min</span>
                  <span>requests: {k.request_count || 0}</span>
                  <span>last used: {fmt(k.last_used_at)}</span>
                  <span>scopes: {(k.scopes || []).join(", ")}</span>
                </div>
                {!revoked && (
                  <div className="flex justify-end pt-3 border-t border-border">
                    <button
                      onClick={() => revoke(k.id)}
                      data-testid={`revoke-agent-key-${k.id}`}
                      className="inline-flex items-center gap-1 px-3 py-2.5 border border-border text-bb-slate text-sm font-archivo uppercase tracking-widest min-h-[44px]"
                    >
                      <ShieldOff size={13} /> Revoke
                    </button>
                  </div>
                )}
              </article>
            );
          })
        )}
      </div>

      <div className="bb-container py-6 text-center">
        <Link to="/" className="text-xs font-archivo uppercase tracking-widest text-bb-slate bb-link">
          Back to site
        </Link>
      </div>
    </div>
  );
}
